// ===========================================
// TABLE SETTINGS MODAL
// ===========================================
// Modal for adjusting sound effects settings
// NOTE: Music controls temporarily disabled - see BACKLOG.md

import { useTableSettingsStore } from '../../stores/tableSettingsStore';
import { Modal } from '../Modal';

// Mute icon SVG components
const VolumeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM18.584 5.106a.75.75 0 011.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 11-1.06-1.06 8.25 8.25 0 000-11.668.75.75 0 010-1.06z" />
    <path d="M15.932 7.757a.75.75 0 011.061 0 6 6 0 010 8.486.75.75 0 01-1.06-1.061 4.5 4.5 0 000-6.364.75.75 0 010-1.06z" />
  </svg>
);

const MutedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM17.78 9.22a.75.75 0 10-1.06 1.06L18.44 12l-1.72 1.72a.75.75 0 001.06 1.06l1.72-1.72 1.72 1.72a.75.75 0 101.06-1.06L20.56 12l1.72-1.72a.75.75 0 00-1.06-1.06l-1.72 1.72-1.72-1.72z" />
  </svg>
);

interface TableSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TableSettingsModal({ isOpen, onClose }: TableSettingsModalProps) {
  const {
    // MUSIC DISABLED - see BACKLOG.md
    // musicEnabled,
    // musicVolume,
    // setMusicEnabled,
    // setMusicVolume,
    sfxVolume,
    setSfxVolume,
    sfxMuted,
    setSfxMuted,
    outcomeVolume,
    setOutcomeVolume,
    outcomeMuted,
    setOutcomeMuted,
  } = useTableSettingsStore();

  if (!isOpen) return null;

  return (
    <Modal
      variant="default"
      blocking={true}
      title="Table Settings"
      onClose={onClose}
      maxWidthClass="max-w-md"
    >
      {/* Content */}
      <div className="p-4 space-y-6">
        {/* MUSIC SECTION DISABLED - see BACKLOG.md
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-white font-medium">Music</span>
            <button
              onClick={() => setMusicEnabled(!musicEnabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                musicEnabled ? 'bg-green-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  musicEnabled ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>

          <div className={`space-y-1 ${!musicEnabled ? 'opacity-50' : ''}`}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Volume</span>
              <span className="text-gray-300">{musicVolume}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={musicVolume}
              onChange={(e) => setMusicVolume(Number(e.target.value))}
              disabled={!musicEnabled}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-500 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        <div className="border-t border-gray-700" />
        END MUSIC SECTION */}

        {/* Sound Effects Section */}
        <div className="space-y-4">
          <span className="text-white font-medium">Sound Effects</span>

          {/* Cards & Chips Volume Slider */}
          <div className={`space-y-1 ${sfxMuted ? 'opacity-50' : ''}`}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Cards & Chips</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-300">{sfxMuted ? 'Muted' : `${sfxVolume}%`}</span>
                <button
                  onClick={() => setSfxMuted(!sfxMuted)}
                  className={`p-1 rounded transition-colors ${
                    sfxMuted ? 'text-red-400 hover:text-red-300' : 'text-gray-400 hover:text-white'
                  }`}
                  title={sfxMuted ? 'Unmute' : 'Mute'}
                >
                  {sfxMuted ? <MutedIcon /> : <VolumeIcon />}
                </button>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={sfxVolume}
              onChange={(e) => setSfxVolume(Number(e.target.value))}
              disabled={sfxMuted}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500 disabled:cursor-not-allowed"
            />
          </div>

          {/* Hand Outcome Volume Slider */}
          <div className={`space-y-1 ${outcomeMuted ? 'opacity-50' : ''}`}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Hand Outcomes</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-300">{outcomeMuted ? 'Muted' : `${outcomeVolume}%`}</span>
                <button
                  onClick={() => setOutcomeMuted(!outcomeMuted)}
                  className={`p-1 rounded transition-colors ${
                    outcomeMuted ? 'text-red-400 hover:text-red-300' : 'text-gray-400 hover:text-white'
                  }`}
                  title={outcomeMuted ? 'Unmute' : 'Mute'}
                >
                  {outcomeMuted ? <MutedIcon /> : <VolumeIcon />}
                </button>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={outcomeVolume}
              onChange={(e) => setOutcomeVolume(Number(e.target.value))}
              disabled={outcomeMuted}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-700">
        <button
          onClick={onClose}
          className="w-full py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
        >
          Done
        </button>
      </div>
    </Modal>
  );
}

export default TableSettingsModal;
